import ContractText from "@/common/ContractText";
import ContractRequestView from "@/common/ContractRequestView";
import FinalContractPdfViewer from "@/common/FinalContractPdfViewer";
import RichContractRenderer from "@/common/RichContractRenderer";
import RenderedContractRenderer from "@/common/RenderedContractRenderer";
import { LayoutHeader } from "@/Header";
import { createFileRoute } from "@tanstack/react-router";
import { useUserMypageContractDetail } from "../../../api/services/customer/myPage";
import { useUserContractRequestDetail } from "~/services/common/contractRequest";

export const Route = createFileRoute("/customer/layout/user-contract-detail")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    const rawSource = String(search.source ?? "").toUpperCase();
    return {
      histContrSeq: Number(search.histContrSeq) || 0,
      requestSeq: Number(search.requestSeq) || 0,
      source: (rawSource === "REQUEST" ? "REQUEST" : "LEGACY") as
        | "LEGACY"
        | "REQUEST",
    };
  },
});

function RouteComponent() {
  const { histContrSeq, requestSeq, source } = Route.useSearch();
  const isRequest = source === "REQUEST" && requestSeq > 0;

  return (
    <>
      <LayoutHeader
        title={"계약서 조회 상세"}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex-auto bg-[#F5F5F5]">
        <div className="flex flex-col flex-auto m-4 bg-white rounded-xl">
          {isRequest ? (
            <RequestBranch requestSeq={requestSeq} />
          ) : (
            <LegacyBranch histContrSeq={histContrSeq} />
          )}
        </div>
      </div>
    </>
  );
}

function LegacyBranch({ histContrSeq }: { histContrSeq: number }) {
  const { data: contractDetail, isLoading, error } =
    useUserMypageContractDetail({ histContrSeq });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState />;
  if (!contractDetail?.data) return <EmptyState />;

  const d = contractDetail.data;
  const variables = {
    guardianName: d.userInfo.userName,
    guardianNickname: d.userInfo.nickname,
    guardianEmail: d.userInfo.email,
    guardianPhone: d.phoneNumber,
    guardianAddress: d.addr ? d.addr + (d.addrDetail || "") : "",
    petName: d.petInfo.petName,
    petBirthday: d.petInfo.petBirthday,
    petBreed: d.petInfo.petBreed,
    petWeight: d.petInfo.petWeight,
    petNeutered: d.petInfo.neuter,
    petVaccinations: d.petInfo.inoculation,
    petPersonality: d.petInfo.personality,
    petAllergies: d.petInfo.allergy,
    petOther: d.petInfo.memo,
    companyName: d.companyInfo.companyName,
    businessNumber: d.companyInfo.businessNumber,
    contractDate: d.dtContract,
    ...(d.variables ?? {}),
  };
  if (d.finalPdfUrl) {
    return (
      <div className="p-4">
        <FinalContractPdfViewer url={d.finalPdfUrl} />
      </div>
    );
  }
  if (d.renderedPageFileUrls?.length && d.placementManifest) {
    return (
      <div className="p-4">
        <RenderedContractRenderer
          pageUrls={d.renderedPageFileUrls}
          placementManifest={d.placementManifest}
          variables={variables}
          signatureImageUrl={d.signatureFileUrl || undefined}
          readOnly
        />
      </div>
    );
  }
  if (d.contractDocJson) {
    return (
      <div className="p-4">
        <RichContractRenderer
          contractDocJson={d.contractDocJson}
          variables={variables}
          signatureImageUrl={d.signatureFileUrl || undefined}
          readOnly
        />
      </div>
    );
  }
  return (
    <ContractText
      guardianName={d.userInfo.userName}
      guardianNickname={d.userInfo.nickname}
      guardianEmail={d.userInfo.email}
      guardianPhone={d.phoneNumber}
      guardianAddress={d.addr ? d.addr + (d.addrDetail || "") : ""}
      petName={d.petInfo.petName}
      petBirthday={d.petInfo.petBirthday}
      petBreed={d.petInfo.petBreed}
      petWeight={d.petInfo.petWeight}
      petNeutered={d.petInfo.neuter}
      petVaccinations={d.petInfo.inoculation}
      petPersonality={d.petInfo.personality}
      petAllergies={d.petInfo.allergy}
      petOther={d.petInfo.memo}
      companyName={d.companyInfo.companyName}
      businessNumber={d.companyInfo.businessNumber}
      contractDate={d.dtContract}
      signatureFileUrl={d.signatureFileUrl || undefined}
    />
  );
}

function RequestBranch({ requestSeq }: { requestSeq: number }) {
  const { data, isLoading, error } = useUserContractRequestDetail(requestSeq);
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState />;
  if (!data?.data) return <EmptyState />;
  return <ContractRequestView detail={data.data} />;
}

const LoadingState = () => (
  <div className="flex justify-center items-center h-64">
    <div className="text-gray-500">로딩 중...</div>
  </div>
);
const ErrorState = () => (
  <div className="flex justify-center items-center h-64">
    <div className="text-red-500">계약서 정보를 불러오는데 실패했습니다.</div>
  </div>
);
const EmptyState = () => (
  <div className="flex justify-center items-center h-64">
    <div className="text-gray-500">계약서 정보가 없습니다.</div>
  </div>
);
